import { Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomTable from "../../../components/CustomTable";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import {
  clearStockTransHistory,
  getTransactionHistoryByStockId,
} from "../../../redux/reducers/clinicSlice";
import { AppColors } from "../../../util/AppColors";
import { transactionTypeList } from "../../../util/dropList";
import { baseURL } from "../../../util/server";

const tblHdrs = [
  "date",
  "tranType",
  "customerSupplier",
  "invoiceNumber",
  "batchNumber",
  // "openingQty",
  "qtySoldPurchased",
  // "closingQty",
  "mrp",
];

const initSearch = {
  startDate: null,
  endDate: null,
  type: "all",
  supplierName: "",
  customerName: "",
  petName: "",
  locationId: "all",
};

const TransactionHistory = ({
  selectedStock = null,
  initialStartDate = null,
  initialEndDate = null,
  initType = null,
}) => {
  const dispatch = useDispatch();
  const [searchValues, setSearchValues] = useState({
    ...initSearch,
    ...(initialStartDate ? { startDate: initialStartDate } : {}),
    ...(initialEndDate ? { endDate: initialEndDate } : {}),
    ...(initType ? { type: initType } : {}),
  });
  const { stockTransHistory, details: clinicDetail } = useSelector(
    (state) => state?.clinic,
  );
  const tranData = stockTransHistory?.data ?? {};
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { clinic: clinicData, locations } = useMemo(() => {
    if (!mandateDataRaw) {
      return { clinic: {}, locations: [] };
    }
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinic: reqData?.clinic ?? {},
        locations: Array.isArray(reqData?.locations) ? reqData.locations : [],
      };
    } catch {
      return { clinic: {}, locations: [] };
    }
  }, [mandateDataRaw]);
  const clinicDet = clinicData?.id ? clinicData : clinicDetail;

  const locationFilterList = useMemo(() => {
    const uniqueLocationIds = [
      ...new Set(
        (tranData?.transactions ?? [])
          ?.map((li) => li?.locationId)
          ?.filter((id) => id != null && id !== ""),
      ),
    ];
    return uniqueLocationIds
      ?.map((locationId) => {
        const loc = locations?.find(
          (li) => String(li?.id) === String(locationId),
        );
        if (!loc?.locationName) return null;
        return {
          name: loc.locationName,
          value: locationId,
        };
      })
      ?.filter(Boolean);
  }, [tranData?.transactions, locations]);

  useEffect(() => {
    dispatch(clearStockTransHistory());

    const timeoutId = setTimeout(() => {
      onApiCall();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    selectedStock?.stockId,
    searchValues?.startDate,
    searchValues?.endDate,
    searchValues?.type,
    searchValues?.supplierName,
    searchValues?.customerName,
    searchValues?.petName,
  ]);

  const getResolvedDateRange = () => {
    const startRaw = searchValues?.startDate;
    const endRaw = searchValues?.endDate;
    if (!startRaw && !endRaw) return { startDate: null, endDate: null };

    const start = startRaw ? moment(new Date(startRaw)) : null;
    let end = endRaw ? moment(new Date(endRaw)) : null;

    if (start && !end) end = start.clone();
    if (!start && end) {
      return {
        startDate: end.format("YYYY-MM-DD"),
        endDate: end.format("YYYY-MM-DD"),
      };
    }
    if (start && end && end.isBefore(start, "day")) {
      return {
        startDate: end.format("YYYY-MM-DD"),
        endDate: start.format("YYYY-MM-DD"),
      };
    }

    return {
      startDate: start?.format("YYYY-MM-DD") ?? null,
      endDate: end?.format("YYYY-MM-DD") ?? null,
    };
  };

  const onApiCall = (reset = false) => {
    if (!selectedStock?.stockId) return;
    const params = new URLSearchParams();
    if (!reset) {
      const { startDate, endDate } = getResolvedDateRange();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (searchValues?.type) params.append("type", searchValues.type);
      if (searchValues?.supplierName?.length > 0) {
        params.append("supplierName", searchValues.supplierName);
      }
      if (searchValues?.customerName?.length > 0) {
        params.append("userName", searchValues.customerName);
      }
      if (searchValues?.petName?.length > 0) {
        params.append("petName", searchValues.petName);
      }
    }
    dispatch(
      getTransactionHistoryByStockId({
        id: selectedStock?.stockId,
        url: `?${params.toString()}`,
      }),
    );
  };

  const onReset = () => {
    flushSync(() => setSearchValues(initSearch));
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({
      ...searchValues,
      [name?.trim()?.replace(/,$/, "")]: value, // remove spaces & trailing commas
    });
  };

  const getFilteredTransactions = () => {
    const transactions = tranData?.transactions ?? [];
    if (!searchValues?.locationId || searchValues.locationId === "all") {
      return transactions;
    }
    return transactions.filter(
      (li) => String(li?.locationId) === String(searchValues.locationId),
    );
  };

  const filteredRecordCount = getFilteredTransactions().length;

  const getTableList = () => {
    return getFilteredTransactions()?.map((li) => {
      let reqQty = Number(li?.quantity ?? 0);
      if (li?.type?.toLowerCase() === "sale") reqQty = -reqQty;
      const closingQty = Number(li?.OpeningQuantity ?? 0) + Number(reqQty);
      return {
        ...li,
        closingQty,
        openingQty: li?.OpeningQuantity ?? 0,
        tranType: li?.type,
        date: moment(new Date(li?.date)).format("DD MMM YYYY"),
        customerSupplier: li?.userName ?? li?.supplierName ?? "",
        qtySoldPurchased: `${li?.quantity}${li?.uom?.length > 0 ? ` ${li?.uom}` : ""}`,
        batchNumber: `${li?.batchNo?.length > 0 ? ` ${li?.batchNo}` : ""}`,
        mrp: `${li?.unitSellingPrice ? ` ${li?.unitSellingPrice}` : ""}`,
      };
    });
  };

  const onDownloadReport = () => {
    const params = new URLSearchParams();
    if (clinicDet?.id) params.append("clinicId", clinicDet.id);
    const { startDate, endDate } = getResolvedDateRange();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (searchValues?.type) params.append("type", searchValues?.type);
    if (searchValues?.supplierName?.length > 0) {
      params.append("supplierName", searchValues?.supplierName);
    }
    if (searchValues?.customerName?.length > 0) {
      params.append("userName", searchValues?.customerName);
    }
    if (searchValues?.petName?.length > 0) {
      params.append("petName", searchValues?.petName);
    }
    if (searchValues?.locationId && searchValues.locationId !== "all") {
      params.append("locationId", searchValues.locationId);
    }
    const queryString = params.toString();
    const reqUrl = `${baseURL}/clinic/stock/${selectedStock?.stockId}/transaction-history/export${
      queryString ? `?${queryString}` : ""
    }`;
    window.open(reqUrl, "_blank");
  };

  return (
    <>
      {/* <TopBar> */}
      <div className="mt20">
        <div className="flex-row">
          <div className="top-row-cen-con w100Per">
            <div className="w13Per">
              <CustomTextField
                placeholder="Start Date"
                fullWidth
                name="startDate"
                handleChange={onSearchChange}
                value={searchValues?.startDate}
                backgroundColor={AppColors.lightBlue2}
                label="Start Date"
                mobileDate
              />
            </div>
            <div className="w13Per ml15">
              <CustomTextField
                placeholder="End Date"
                fullWidth
                name="endDate"
                handleChange={onSearchChange}
                value={searchValues?.endDate}
                backgroundColor={AppColors.lightBlue2}
                label="End Date"
                mobileDate
              />
            </div>
            <div className="w13Per ml15">
              <CustomSelect
                list={[
                  ...transactionTypeList,
                  { name: "Transfers", value: "TRANSFER" },
                  { name: "Consumption", value: "consumption" },
                ]}
                fullWidth
                name="type"
                handleChange={onSearchChange}
                value={searchValues?.type}
                backgroundColor={AppColors.lightBlue2}
                label="Type"
                select
              />
            </div>
            <div className="w13Per ml15">
              <CustomTextField
                placeholder="Supplier Name"
                fullWidth
                name="supplierName"
                handleChange={onSearchChange}
                value={searchValues?.supplierName}
                backgroundColor={AppColors.lightBlue2}
                label="Supplier Name"
              />
            </div>
            <div className="w13Per ml15">
              <CustomTextField
                placeholder="Customer Name"
                fullWidth
                name="customerName"
                handleChange={onSearchChange}
                value={searchValues?.customerName}
                backgroundColor={AppColors.lightBlue2}
                label="Customer Name"
              />
            </div>
            <div className="w13Per ml15">
              <CustomTextField
                placeholder="Pet Name"
                fullWidth
                name="petName"
                handleChange={onSearchChange}
                value={searchValues?.petName}
                backgroundColor={AppColors.lightBlue2}
                label="Pet Name"
              />
            </div>
            <div className="w13Per ml15">
              <CustomSelect
                list={[{ name: "All", value: "all" }, ...locationFilterList]}
                fullWidth
                name="locationId"
                handleChange={onSearchChange}
                value={searchValues?.locationId ?? "all"}
                backgroundColor={AppColors.lightBlue2}
                label="Location"
                select
              />
            </div>

            <div className="ml15">
              <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
            </div>
            <div className="ml15">
              <CustomButton text="⬇" onClick={onDownloadReport} smallBtn />
            </div>
          </div>
        </div>
        <div className="flex-row-ali-cen mt10 mh30">
          <Typography className="txt-semi-bold fs14">
            {tranData?.tradeName ?? ""}
          </Typography>
          <Typography className="txt-semi-bold fs14 ml15">
            {tranData?.productName ?? ""}
          </Typography>
        </div>
        <div className="flex-row-ali-cen mh30">
          <Typography className="font-medium fs14">
            Current stock Qty: {tranData?.currentStockQty ?? ""}
          </Typography>
          <Typography className="font-medium fs14 ml15">
            Total purchase Qty: {tranData?.totalPurchaseQty ?? ""}
          </Typography>
          <Typography className="font-medium fs14 ml15">
            Total sales Qty: {tranData?.totalSalesQty ?? ""}
          </Typography>
          {/* <Typography className="font-medium fs14 ml15">
            Opening Qty: {tranData?.totalSalesQty ?? ""}
          </Typography>
          <Typography className="font-medium fs14 ml15">
            Closing Qty: {tranData?.totalSalesQty ?? ""}
          </Typography> */}
        </div>
      </div>
      <Typography className="font-medium fs12 gray28 mt10 mh30">
        Number of records: {filteredRecordCount}
      </Typography>
      <div className="mtMin10">
        <CustomTable
          grey
          columns={tblHdrs}
          datas={getTableList()}
          rowsPerPage={filteredRecordCount || 1}
          totalRecords={filteredRecordCount}
        />
      </div>
    </>
  );
};

export default TransactionHistory;
